import { IQuoteSettingsConfigSchema } from '@monetize/types/app';
import { useEffect } from 'react';
import {
  MAccordion,
  MAccordionCustomButton,
  MFormField,
  MRadio,
  MRadioGroup,
  MText,
} from '../../../../../components/Monetize';
import { MSettingAccordionItem } from '../../../../../components/Monetize/MSettingAccordionItem';
import { useAccordionControl } from '../../../../../hooks';
import { useAsyncStatus } from '../../../../../hooks/useAsyncStatus';
import { useScrollToHighlightedSection } from '../../../../../hooks/useScrollToHighlightedSection';
import { getHighlightStyles } from '../../../../../hooks/useSettingsSectionHighlight';
import { useSettingsSectionHighlights } from '../../../../../hooks/useSettingsSectionHighlights';
import { SettingsSection } from '../../../components/SettingsSection';
import { SettingsToggle } from '../../../components/SettingsToggle';
import {
  QUOTE_SETTING_KEYS,
  getQuoteSettingsSectionKeys,
} from '../quoteSettingsConfig';
import { AddressSourceSetting } from './AddressSourceSetting';
import { DisplayUnitPriceFrequency } from './DisplayUnitPriceFrequency';

interface QuoteSettingGeneralProps {
  quoteSettings: IQuoteSettingsConfigSchema;
  onSubmit: (data: Partial<IQuoteSettingsConfigSchema>) => Promise<void>;
  isLoading: boolean;
}

export const QuoteSettingGeneral = ({
  quoteSettings,
  onSubmit,
  isLoading,
}: QuoteSettingGeneralProps) => {
  const { accordionValue, handleAccordionChange, setAccordionValue } =
    useAccordionControl();

  const poNumberStatus = useAsyncStatus();
  const vatNumberStatus = useAsyncStatus();
  const esignStatus = useAsyncStatus();
  const displayFrequencyStatus = useAsyncStatus();
  const highlights = useSettingsSectionHighlights(
    getQuoteSettingsSectionKeys(QUOTE_SETTING_KEYS.general),
  );

  const shouldOpenGeneral = Object.values(highlights).some(Boolean);
  useEffect(() => {
    if (shouldOpenGeneral) {
      setAccordionValue(['0']);
    }
  }, [shouldOpenGeneral, setAccordionValue]);

  useScrollToHighlightedSection(highlights, !isLoading);

  return (
    <MAccordion
      multiple
      data-testid="quote-setting-general"
      w="100%"
      value={accordionValue}
      onValueChange={({ value }) => handleAccordionChange(value)}
    >
      <MSettingAccordionItem
        value="0"
        id={QUOTE_SETTING_KEYS.general}
        {...getHighlightStyles(highlights[QUOTE_SETTING_KEYS.general])}
        renderAccordionButton={({ isExpanded }: { isExpanded: boolean }) => (
          <MAccordionCustomButton isExpanded={isExpanded} label="General" />
        )}
        borderWidth="1px"
      >
        <SettingsSection
          label="Length"
          id={QUOTE_SETTING_KEYS.quoteGeneralLength}
          {...getHighlightStyles(
            highlights[QUOTE_SETTING_KEYS.quoteGeneralLength],
          )}
        >
          <MText mb="2">Select the contract length unit of measurement:</MText>
          <MFormField onClick={(ev) => ev.stopPropagation()} w="auto">
            <MRadioGroup
              onChange={(val: string) =>
                onSubmit({
                  customContractLength: {
                    allowCustomLength: val === 'true' ? true : false,
                  },
                })
              }
              display="flex"
              flexDirection="column"
              rowGap={2}
              value={
                quoteSettings?.customContractLength.allowCustomLength
                  ? 'true'
                  : 'false'
              }
            >
              <MRadio value="true">
                Any number of months (e.g. 15 months)
              </MRadio>
              <MRadio value="false">
                Whole years only (1 year, 2 year, etc.)
              </MRadio>
            </MRadioGroup>
          </MFormField>
        </SettingsSection>

        <SettingsSection
          id={QUOTE_SETTING_KEYS.quoteGeneralAddressSource}
          {...getHighlightStyles(
            highlights[QUOTE_SETTING_KEYS.quoteGeneralAddressSource],
          )}
        >
          <AddressSourceSetting
            isLoading={isLoading}
            value={quoteSettings?.defaultAddressSource}
            onChange={(defaultAddressSource) =>
              onSubmit({
                defaultAddressSource,
              })
            }
          />
        </SettingsSection>

        <SettingsSection
          showDivider={true}
          id={QUOTE_SETTING_KEYS.quoteGeneralAllowPoNumber}
          {...getHighlightStyles(
            highlights[QUOTE_SETTING_KEYS.quoteGeneralAllowPoNumber],
          )}
        >
          <SettingsToggle
            name="allowPONumberOnQuotes"
            heading="Allow Purchase Order (PO) Number"
            subheading="Allow users to add a PO number on quote."
            fetchLoading={isLoading}
            value={quoteSettings?.allowPONumberOnQuotes!}
            asyncStatus={poNumberStatus.status}
            onChange={(allowPONumberOnQuotes) =>
              poNumberStatus.track(() => onSubmit({ allowPONumberOnQuotes }))
            }
          />
        </SettingsSection>

        <SettingsSection
          showDivider={true}
          id={QUOTE_SETTING_KEYS.quoteGeneralAllowVatNumber}
          {...getHighlightStyles(
            highlights[QUOTE_SETTING_KEYS.quoteGeneralAllowVatNumber],
          )}
        >
          <SettingsToggle
            name="allowVATNumberOnQuotes"
            heading="Allow VAT Number"
            subheading="Allow users to add VAT number on quote."
            fetchLoading={isLoading}
            value={quoteSettings?.allowVATNumberOnQuotes!}
            asyncStatus={vatNumberStatus.status}
            onChange={(allowVATNumberOnQuotes) =>
              vatNumberStatus.track(() => onSubmit({ allowVATNumberOnQuotes }))
            }
          />
        </SettingsSection>

        <SettingsSection
          showDivider={true}
          id={QUOTE_SETTING_KEYS.quoteGeneralAllowAdjustingEsignToggle}
          {...getHighlightStyles(
            highlights[
              QUOTE_SETTING_KEYS.quoteGeneralAllowAdjustingEsignToggle
            ],
          )}
        >
          <SettingsToggle
            name="allowEsignEditOnQuoteReview"
            heading="Allow Adjusting ESign Toggle During Quote Review"
            subheading="Allow eSign to be enabled or disabled on a Quote without going back to edit mode."
            fetchLoading={isLoading}
            value={quoteSettings?.allowEsignEditOnQuoteReview ?? false}
            asyncStatus={esignStatus.status}
            onChange={(allowEsignEditOnQuoteReview) =>
              esignStatus.track(() => onSubmit({ allowEsignEditOnQuoteReview }))
            }
          />
        </SettingsSection>

        <SettingsSection
          showDivider={false}
          id={QUOTE_SETTING_KEYS.quoteGeneralOverridePricesDisplay}
          {...getHighlightStyles(
            highlights[QUOTE_SETTING_KEYS.quoteGeneralOverridePricesDisplay],
          )}
        >
          <DisplayUnitPriceFrequency
            value={quoteSettings?.defaultDisplayUnitPriceFrequency}
            asyncStatus={displayFrequencyStatus.status}
            onChange={(defaultDisplayUnitPriceFrequency) =>
              displayFrequencyStatus.track(() =>
                onSubmit({ defaultDisplayUnitPriceFrequency }),
              )
            }
            isLoading={isLoading}
          />
        </SettingsSection>
      </MSettingAccordionItem>
    </MAccordion>
  );
};
